import React, { useState, useEffect } from 'react';
import { ArrowLeft, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';

interface ResetPasswordProps {
  onNavigate: (page: string) => void;
}

const ResetPassword: React.FC<ResetPasswordProps> = ({ onNavigate }) => {
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Get token from URL
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      setToken(urlToken);
    } else {
      setMessage('Invalid reset link');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setMessage('Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    setMessage('');
    
    try {
      const response = await fetch('https://sparklinggems.shop/api/auth/reset-password.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(true);
        setMessage('Password updated successfully! Redirecting to login...');
        setTimeout(() => onNavigate('auth'), 3000);
      } else {
        setMessage(data.message || 'Failed to reset password');
      }
    } catch (error) {
      setMessage('Connection error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
      <div className="bg-white w-full max-w-md p-8 shadow-xl">
        <button 
          onClick={() => onNavigate('auth')}
          className="flex items-center gap-2 text-stone-400 hover:text-stone-900 text-xs uppercase tracking-widest mb-6"
        >
          <ArrowLeft size={14} /> Back to Login
        </button>
        
        <h1 className="font-serif text-3xl text-stone-900 mb-2">Reset Password</h1>
        <p className="text-stone-400 text-sm mb-8">Enter your new password below</p>
        
        {message && (
          <div className={`p-3 rounded flex items-center gap-3 text-sm mb-6 ${
            success ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
          }`}>
            {success ? <Check size={16} /> : <AlertCircle size={16} />}
            <span>{message}</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="group relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent pr-8"
              placeholder="New Password"
              id="new-password"
            />
            <label htmlFor="new-password" className="absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs">
              New Password
            </label>
            <button 
              type="button" 
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-0 top-3 text-stone-400 hover:text-stone-600"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          
          <div className="group relative">
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent"
              placeholder="Confirm Password"
              id="confirm-password"
            />
            <label htmlFor="confirm-password" className="absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs">
              Confirm Password
            </label>
          </div>
          
          <button
            type="submit"
            disabled={isLoading || !token}
            className="w-full bg-stone-900 text-white py-4 uppercase text-xs tracking-widest hover:bg-gold-600 transition-colors disabled:opacity-50 mt-8"
          >
            {isLoading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;